import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

/* ---------- types ---------- */
interface DashboardStats {
  total_monitors: number;
  monitors_clocked_in: number;
  total_shifts_today: number;
  total_alerts_today: number;
  total_activities_today: number;
  total_hours_today: number;
  total_shifts_this_week: number;
  total_alerts_this_week: number;
  total_hours_this_week: number;
}

interface DashboardMonitor {
  id: number;
  full_name: string;
  email: string;
  role: string;
  is_clocked_in: boolean;
  current_shift_start: string | null;
  shifts_today: number;
  alerts_today: number;
  activities_today: number;
  hours_today: number;
  shifts_this_week: number;
  alerts_this_week: number;
  activities_this_week: number;
  hours_this_week: number;
  assigned_stores_count: number;
}

interface AdminStore {
  id: number;
  name: string;
  address: string;
  assigned_monitor_count: number;
  latest_alert_at: string | null;
}

export default function AdminPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [monitors, setMonitors] = useState<DashboardMonitor[]>([]);
  const [stores, setStores] = useState<AdminStore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'monitors' | 'stores' | 'coverage'>('monitors');

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      try {
        const [statsRes, monRes, storeRes] = await Promise.all([
          api.get('/admin/dashboard/stats'),
          api.get('/admin/dashboard/monitors'),
          api.get('/admin/stores'),
        ]);
        if (!cancelled) {
          setStats(statsRes.data);
          setMonitors(monRes.data.monitors);
          setStores(storeRes.data.stores);
          setLoading(false);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(extractErrorMessage(err, 'Failed to load admin data.'));
          setLoading(false);
        }
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, []);

  // Sort: clocked-in first, then by alerts_today descending
  const sortedMonitors = [...monitors].sort((a, b) => {
    if (a.is_clocked_in !== b.is_clocked_in) return a.is_clocked_in ? -1 : 1;
    return b.alerts_today - a.alerts_today;
  });

  if (!isAdmin) {
    return (
      <div style={styles.page}>
        <main style={styles.main}>
          <div style={styles.accessDenied}>
            <span style={styles.accessDeniedIcon}>&#9888;</span>
            <h2 style={{ margin: '0 0 8px', fontSize: 'var(--text-xl)', fontFamily: 'var(--font-display)', color: 'var(--status-critical)' }}>Access Denied</h2>
            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)' }}>Lead or Admin role required to view this page.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <div style={styles.titleRow}>
          <h2 style={styles.pageTitle}>Admin Dashboard</h2>
        </div>

        {loading && <p style={styles.statusText}>Loading admin data...</p>}
        {error && <div style={styles.errorBanner}>{error}</div>}

        {!loading && !error && stats && (
          <>
            {/* Today stats */}
            <div style={styles.sectionLabel}>Today</div>
            <div style={styles.statsGrid5}>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.monitors_clocked_in}</div>
                <div style={styles.statLabel}>Monitors Online</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.total_shifts_today}</div>
                <div style={styles.statLabel}>Shifts Today</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.total_alerts_today}</div>
                <div style={styles.statLabel}>Alerts Sent</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.total_activities_today}</div>
                <div style={styles.statLabel}>Activities Logged</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{typeof stats.total_hours_today === 'number' ? stats.total_hours_today.toFixed(1) : stats.total_hours_today}</div>
                <div style={styles.statLabel}>Hours Covered</div>
              </div>
            </div>

            {/* This Week stats */}
            <div style={styles.sectionLabel}>This Week</div>
            <div style={styles.statsGrid3}>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.total_shifts_this_week}</div>
                <div style={styles.statLabel}>Shifts</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{stats.total_alerts_this_week}</div>
                <div style={styles.statLabel}>Alerts</div>
              </div>
              <div style={styles.statCard}>
                <div style={styles.statValue}>{typeof stats.total_hours_this_week === 'number' ? stats.total_hours_this_week.toFixed(1) : stats.total_hours_this_week}</div>
                <div style={styles.statLabel}>Hours</div>
              </div>
            </div>

            {/* Tab buttons */}
            <div style={styles.tabRow}>
              <button
                onClick={() => setTab('monitors')}
                style={tab === 'monitors' ? { ...styles.tabBtn, ...styles.tabBtnActive } : styles.tabBtn}
              >
                Monitors ({monitors.length})
              </button>
              <button
                onClick={() => setTab('stores')}
                style={tab === 'stores' ? { ...styles.tabBtn, ...styles.tabBtnActive } : styles.tabBtn}
              >
                All Stores ({stores.length})
              </button>
              <button
                onClick={() => navigate('/admin/coverage')}
                style={styles.tabBtn}
              >
                Coverage &#8594;
              </button>
            </div>

            {/* Monitors tab - table */}
            {tab === 'monitors' && (
              <div style={styles.tableContainer}>
                {monitors.length === 0 && <p style={styles.statusText}>No monitors found.</p>}
                {monitors.length > 0 && (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Name</th>
                        <th style={styles.th}>Status</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Alerts Today</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Activities Today</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Hours Today</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Alerts This Week</th>
                        <th style={styles.th}>Stores</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMonitors.map((m) => (
                        <tr
                          key={m.id}
                          style={styles.tr}
                          onClick={() => navigate(`/admin/monitors/${m.id}`)}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--table-row-hover)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                        >
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={styles.monitorName}>{m.full_name}</span>
                              <span style={roleBadgeStyle(m.role)}>{m.role}</span>
                            </div>
                          </td>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: m.is_clocked_in ? 'var(--status-success)' : 'var(--text-tertiary)',
                                boxShadow: m.is_clocked_in ? '0 0 6px rgba(23,178,106,0.5)' : 'none',
                                flexShrink: 0,
                              }} />
                              <span style={{ fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)', color: m.is_clocked_in ? 'var(--status-success)' : 'var(--text-secondary)' }}>
                                {m.is_clocked_in ? 'On Shift' : 'Off'}
                              </span>
                            </div>
                          </td>
                          <td style={{ ...styles.td, textAlign: 'right', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{m.alerts_today}</td>
                          <td style={{ ...styles.td, textAlign: 'right', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{m.activities_today}</td>
                          <td style={{ ...styles.td, textAlign: 'right', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{typeof m.hours_today === 'number' ? m.hours_today.toFixed(1) : m.hours_today}</td>
                          <td style={{ ...styles.td, textAlign: 'right', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' }}>{m.alerts_this_week}</td>
                          <td style={{ ...styles.td, fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                            {m.assigned_stores_count} store{m.assigned_stores_count !== 1 ? 's' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* Stores tab */}
            {tab === 'stores' && (
              <div style={styles.listContainer}>
                {stores.length === 0 && <p style={styles.statusText}>No stores found.</p>}
                {stores.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      ...styles.listItem,
                      ...(s.assigned_monitor_count === 0 ? styles.listItemWarning : {}),
                    }}
                  >
                    <div style={styles.listItemLeft}>
                      <div style={styles.storeRow}>
                        <span style={styles.storeName}>{s.name}</span>
                        {s.assigned_monitor_count === 0 && (
                          <span style={styles.warningBadge}>Unassigned</span>
                        )}
                      </div>
                      <div style={styles.storeMeta}>
                        {s.address}
                        <span style={styles.metaSep}>&middot;</span>
                        {s.assigned_monitor_count} monitor{s.assigned_monitor_count !== 1 ? 's' : ''}
                        {s.latest_alert_at && (
                          <>
                            <span style={styles.metaSep}>&middot;</span>
                            Last alert: {new Date(s.latest_alert_at).toLocaleDateString()}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

function roleBadgeStyle(role: string): React.CSSProperties {
  const map: Record<string, { bg: string; color: string }> = {
    admin: { bg: 'var(--accent-muted)', color: 'var(--accent-text)' },
    lead: { bg: 'var(--status-warning-muted)', color: 'var(--status-warning)' },
    monitor: { bg: 'var(--status-neutral-muted)', color: 'var(--text-secondary)' },
  };
  const colors = map[role] || map.monitor;
  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    background: colors.bg,
    color: colors.color,
    textTransform: 'capitalize',
  };
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-base)',
  },
  main: {
    maxWidth: 1060,
    margin: '0 auto',
    padding: 'var(--space-8) var(--space-6)',
  },
  titleRow: {
    marginBottom: 'var(--space-6)',
  },
  pageTitle: {
    fontSize: 'var(--text-xl)',
    fontWeight: 600,
    fontFamily: 'var(--font-display)',
    color: 'var(--text-primary)',
    margin: 0,
  },
  statusText: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '14px 18px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-base)',
    fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)',
  },
  accessDenied: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-xl)',
    border: '1px solid rgba(255,71,87,0.3)',
    padding: '40px 32px',
    textAlign: 'center',
  },
  accessDeniedIcon: {
    fontSize: 36,
    color: 'var(--status-critical)',
    display: 'block',
    marginBottom: 'var(--space-3)',
  },

  // Section labels
  sectionLabel: {
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 'var(--space-2)',
  },

  // Stats grids
  statsGrid5: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-6)',
  },
  statsGrid3: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 'var(--space-3)',
    marginBottom: 'var(--space-8)',
  },
  statCard: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
    borderLeft: '3px solid var(--accent)',
    padding: '20px 16px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: 'var(--text-2xl)',
    fontWeight: 700,
    fontFamily: 'var(--font-mono)',
    color: 'var(--text-primary)',
    lineHeight: 1,
    marginBottom: 6,
  },
  statLabel: {
    fontSize: 'var(--text-xs)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },

  // Tabs
  tabRow: {
    display: 'flex',
    gap: 'var(--space-2)',
    marginBottom: 'var(--space-4)',
  },
  tabBtn: {
    padding: '8px 18px',
    border: 'none',
    background: 'transparent',
    fontSize: 'var(--text-base)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    borderRadius: 'var(--radius-md)',
    transition: 'background 0.15s, color 0.15s',
  },
  tabBtnActive: {
    color: 'var(--text-primary)',
    background: 'var(--bg-surface-raised)',
  },

  // Table
  tableContainer: {
    overflowX: 'auto',
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'var(--font-body)',
  },
  th: {
    textAlign: 'left',
    padding: '10px 14px',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    background: 'var(--table-header-bg)',
    borderBottom: '1px solid var(--border-default)',
  },
  tr: {
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  td: {
    padding: '12px 14px',
    fontSize: 'var(--text-base)',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-subtle)',
  },
  monitorName: {
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },

  // Stores list
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    borderRadius: 'var(--radius-lg)',
    overflow: 'hidden',
    border: '1px solid var(--border-subtle)',
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'var(--bg-surface)',
    borderBottom: '1px solid var(--border-subtle)',
    padding: '14px 18px',
  },
  listItemWarning: {
    borderLeft: '3px solid var(--status-critical)',
  },
  listItemLeft: {
    flex: 1,
    minWidth: 0,
  },
  storeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  storeName: {
    fontSize: 'var(--text-md)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
  },
  storeMeta: {
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    marginLeft: 0,
  },
  metaSep: {
    margin: '0 6px',
  },
  warningBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
  },
};
